import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { PlayCircle, Loader2, ShieldCheck, Download, AlertTriangle, CheckCircle2 } from 'lucide-react';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', blue: '#1A4C7C' };
const n = (v) => (v || 0).toLocaleString();

export function MappingDryRun({ api, set, outstanding, onChanged, isAdmin }) {
  const [file, setFile] = useState(null);
  const [run, setRun] = useState(null);
  const [agreed, setAgreed] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const readOnly = set.status !== 'draft' || !isAdmin;
  const approved = set.status === 'approved';
  const last = set.last_dry_run;

  const dryRun = async () => {
    const fd = new FormData();
    fd.append('file', file);
    setBusy('dry');
    try {
      const { data } = await api.post(`/migration/mappings/${set.set_id}/dry-run`, fd, { timeout: 180000 });
      setRun(data);
      toast[data.rejected ? 'warning' : 'success'](
        `${n(data.accepted)} of ${n(data.rows)} rows would land (${data.acceptance_pct}%)`);
      onChanged();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'The dry run failed');
    }
    setBusy('');
  };

  const approve = async () => {
    setBusy('approve');
    try {
      await api.post(`/migration/mappings/${set.set_id}/approve`, { agreed_with: agreed, note });
      toast.success('Approved — this version is now frozen on the record');
      onChanged();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.outstanding) toast.error(`${d.message}: ${d.outstanding[0]}`, { duration: 9000 });
      else toast.error(typeof d === 'string' ? d : 'Could not approve');
    }
    setBusy('');
  };

  const revise = async () => {
    setBusy('revise');
    try {
      const { data } = await api.post(`/migration/mappings/${set.set_id}/revise`);
      toast.success(`Version ${data.version} opened in draft — version ${set.version} stays as agreed`);
      onChanged(data.set_id);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Could not open a new version');
    }
    setBusy('');
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/migration/mappings/${set.set_id}/export.csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `mapping-${set.entity}-v${set.version}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Mapping document exported');
    } catch { toast.error('Export failed'); }
  };

  const result = run || last;

  return (
    <div className="space-y-3">
      <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)' }}
        data-testid="dry-run-card">
        <h3 className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: C.slate }}>Dry run</h3>
        <p className="text-[11px] mb-2" style={{ color: C.slate }}>
          A map nobody has seen run cannot be approved. Upload the extract and it is put through the map
          <b> in memory</b> — you get the canonical records it would produce and the exact count of what each
          rule would reject. Nothing is written, and the rows are gone the moment it finishes.
        </p>
        {!readOnly && (
          <div className="flex items-center gap-2 mb-2">
            <input type="file" accept=".csv,.xlsx" data-testid="dry-run-file-input"
              onChange={e => setFile(e.target.files?.[0] || null)} className="text-xs" />
            <Button size="sm" onClick={dryRun} disabled={!file || busy === 'dry'} className="text-[11px] rounded-sm h-7"
              style={{ background: C.blue }} data-testid="dry-run-btn">
              {busy === 'dry' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <PlayCircle className="w-3 h-3 mr-1" />}
              Run the map
            </Button>
          </div>
        )}

        {result && (
          <div data-testid="dry-run-result">
            <div className="flex flex-wrap items-center gap-4 mb-2">
              <span className="text-sm font-mono font-semibold" style={{ color: result.acceptance_pct >= 95 ? C.green : result.acceptance_pct >= 75 ? C.amber : C.red }}>
                {result.acceptance_pct}% would land
              </span>
              <span className="text-[11px]" style={{ color: C.slate }}>
                {n(result.accepted)} accepted · <b style={{ color: result.rejected ? C.red : C.slate }}>{n(result.rejected)} rejected</b> of {n(result.rows)} rows
                {result.filename ? ` · ${result.filename}` : ''} · {(result.at || '').slice(0, 16).replace('T', ' ')}
              </span>
            </div>
            {!!(result.missing_source_columns || []).length && (
              <p className="text-[11px] mb-1.5" style={{ color: C.red }} data-testid="dry-run-missing-columns">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                The map expects columns this file does not have: {result.missing_source_columns.join(', ')}
              </p>
            )}
            {(result.failures || []).map(f => (
              <p key={f.canonical_field} className="text-[11px]" data-testid={`dry-run-failure-${f.canonical_field}`}>
                <span style={{ color: f.required ? C.red : C.amber }}>■</span>{' '}
                <b style={{ color: C.navy }}>{f.label}</b> <span style={{ color: C.slate }}>
                  — {n(f.count)} rows rejected by <span className="font-mono">{f.transform}</span>: {(f.examples || []).join('; ')}
                </span>
              </p>
            ))}
            {run?.samples?.length > 0 && (
              <div className="mt-2 overflow-x-auto" data-testid="dry-run-samples">
                <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: C.slate }}>
                  Canonical records this map produces
                </p>
                <table className="text-[10px]">
                  <thead><tr className="text-left" style={{ color: C.slate }}>
                    {Object.keys(run.samples[0]).map(k => <th key={k} className="pr-3 py-0.5">{k}</th>)}
                  </tr></thead>
                  <tbody>
                    {run.samples.map((s, i) => (
                      <tr key={i} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
                        {Object.keys(run.samples[0]).map(k => (
                          <td key={k} className="pr-3 py-0.5 font-mono" style={{ color: C.navy }}>
                            {String(s[k] ?? '—').slice(0, 26)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {run?.note && <p className="text-[10px] mt-1" style={{ color: C.green }}>{run.note}</p>}
          </div>
        )}
      </Card>

      <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)' }}
        data-testid="approval-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: C.slate }}>
              {approved ? 'Approved' : 'Approval'}
            </h3>
            {approved ? (
              <div className="text-[11px]" style={{ color: C.navy }} data-testid="approval-record">
                <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" style={{ color: C.green }} />
                Version {set.version} approved by <b>{set.approved?.by?.name || set.approved?.by?.email}</b>
                {set.approved?.by?.role ? ` (${set.approved.by.role.replace(/_/g, ' ')})` : ''} on{' '}
                {(set.approved?.at || '').slice(0, 16).replace('T', ' ')}
                {set.approved?.agreed_with ? <> · agreed with <b>{set.approved.agreed_with}</b></> : ''}
                {set.approved?.note ? <div className="mt-0.5" style={{ color: C.slate }}>“{set.approved.note}”</div> : null}
                <div className="mt-0.5" style={{ color: C.slate }}>
                  Approved against a dry run of {n(set.approved?.dry_run?.rows)} rows at {set.approved?.dry_run?.acceptance_pct}% acceptance.
                </div>
                {set.superseded_by && <div className="mt-0.5" style={{ color: C.amber }}>A newer version exists — this one stays as what was agreed at the time.</div>}
              </div>
            ) : readOnly ? (
              <p className="text-[11px]" style={{ color: C.slate }} data-testid="approval-read-only">
                This map is still in draft. Proposing, running and approving a map is an admin action —
                {' '}{outstanding?.length || 0} thing{(outstanding?.length || 0) === 1 ? '' : 's'} still to decide.
              </p>
            ) : outstanding?.length ? (
              <div data-testid="approval-outstanding">
                <p className="text-[11px] mb-1" style={{ color: C.red }}>
                  {outstanding.length} thing{outstanding.length > 1 ? 's' : ''} to decide before this can be approved:
                </p>
                <ul className="text-[11px] space-y-0.5" style={{ color: C.navy }}>
                  {outstanding.slice(0, 12).map((o, i) => <li key={i} data-testid={`outstanding-${i}`}>· {o}</li>)}
                </ul>
              </div>
            ) : (
              <div className="space-y-1.5" data-testid="approval-ready">
                <p className="text-[11px]" style={{ color: C.green }}>
                  Everything is decided and the map has been seen to run. Approving freezes this version with your
                  name, your role and the time against it.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  <Input value={agreed} onChange={e => setAgreed(e.target.value)} data-testid="agreed-with-input"
                    placeholder="agreed with (client data owner)" className="h-7 text-[11px] rounded-sm w-56" />
                  <Input value={note} onChange={e => setNote(e.target.value)} data-testid="approval-note-input"
                    placeholder="note for the record" className="h-7 text-[11px] rounded-sm flex-1 min-w-[180px]" />
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1.5 shrink-0">
            {!readOnly && !outstanding?.length && (
              <Button size="sm" onClick={approve} disabled={busy === 'approve'} className="text-[11px] rounded-sm h-7"
                style={{ background: C.green }} data-testid="approve-btn">
                {busy === 'approve' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
                Approve version {set.version}
              </Button>
            )}
            {approved && isAdmin && !set.superseded_by && (
              <Button size="sm" variant="outline" onClick={revise} disabled={busy === 'revise'}
                className="text-[11px] rounded-sm h-7" style={{ color: C.blue }} data-testid="revise-btn">
                {busy === 'revise' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                New version from this
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={exportCsv} className="text-[11px] rounded-sm h-7"
              data-testid="mapping-export-btn"><Download className="w-3 h-3 mr-1" />Mapping document</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
